import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useSuperAdmin } from './hooks/useSuperAdmin';
import { useSubscription } from './hooks/useSubscription';
import Layout from './components/layout/Layout';
import AuthPage from './components/auth/AuthPage';
import Dashboard from './components/dashboard/Dashboard';
import MedicinesList from './components/medicines/MedicinesList';
import AddMedicineForm from './components/medicines/AddMedicineForm';
import FamilyMembers from './components/family/FamilyMembers';
import Schedule from './components/schedule/Schedule';
import Reports from './components/reports/Reports';
import Settings from './components/settings/Settings';
import SubscriptionPage from './components/subscription/SubscriptionPage';
import TrialBanner from './components/subscription/TrialBanner';
import SuperAdminLogin from './components/admin/SuperAdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { userSubscription } = useSubscription();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  // Block access if trial expired
  if (userSubscription?.status === 'expired') {
    return <Navigate to="/subscription" />;
  }

  return (
    <>
      <TrialBanner />
      <Layout>{children}</Layout>
    </>
  );
};

const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useSuperAdmin();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return isAdmin ? children : <Navigate to="/admin/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#10b981',
                },
              },
              error: {
                style: {
                  background: '#ef4444',
                },
              },
            }}
          />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            
            {/* Public Routes */}
            <Route path="/auth" element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            } />
            
            <Route path="/subscription" element={<SubscriptionPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<SuperAdminLogin />} />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            
            {/* Protected App Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/medicines" element={
              <ProtectedRoute>
                <MedicinesList />
              </ProtectedRoute>
            } />
            
            <Route path="/medicines/add" element={
              <ProtectedRoute>
                <AddMedicineForm />
              </ProtectedRoute>
            } />
            
            <Route path="/family" element={
              <ProtectedRoute>
                <FamilyMembers />
              </ProtectedRoute>
            } />
            
            <Route path="/schedule" element={
              <ProtectedRoute>
                <Schedule />
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;